import { useState, MouseEvent } from 'react';
import { toast } from 'react-hot-toast';
import { useTheme } from 'styled-components';
import { AudioSchema } from '../../../API/types/AudioSchemas';
import AudioThumbnail from '../../../assets/images/audio-thumbnail.png';
import Modal from '../../../components/common/Modal/Modal';
import Tags from '../../../components/common/Tags';
import { Button } from '../../../components/ui/Button/Button';
import { ListItemWrapper } from '../../../components/ui/ListItemWrapper/ListItemWrapper';
import { getFormattedDate } from '../../../utils/dateUtils';
import { IconKeys } from '../../../components/common/Icon/IconKeys';
import useGetRole from '../../../API/hooks/useGetRole';
import { ThemeType } from '../../../theme/themeType';
import DeleteModal from '../../../components/common/DeleteModal/DeleteModal';
import AlertSchema from './AudioSchema/AlertSchema';
import useGetAudioSchemaFleet from '../../../API/hooks/devices/useGetAudioSchemaFleet';
import useGetFleetId from '../../../API/hooks/useGetFleetId';
import useDeleteAudioSchema from '../../../API/hooks/devices/useDeleteAudioSchema';
import UpdateSchemaModal from './UpdateSchemaModal/UpdateSchemaModal';
import usePostApplyAudioSchemaToFleet from '../../../API/hooks/devices/usePostApplyAudioSchemaToFleet';

type Props = {
  item?: AudioSchema;
};
function AlertAudioItem({ item }: Props): JSX.Element {
  const theme = useTheme() as ThemeType;
  const {
    isFleetAdmin, isSupport, isAdmin,
  } = useGetRole();
  const fleetId = useGetFleetId();
  const canEdit = isFleetAdmin || isSupport || isAdmin;
  const onDeleteSuccess = (): any => {
    toast.success('Schema was removed');
  };
  const onDeleteFail = (errorMessage: string): any => {
    toast.error(`Schema removing failed: ${errorMessage}`);
  };
  const onApplySuccess = (): any => {
    toast.success('Schema was applied to fleet');
  };
  const onApplyFail = (errorMessage: string): any => {
    toast.error(`Schema applying failed: ${errorMessage}`);
  };
  const [modalEditSchema, toggleModalEditSchema] = useState(false);
  const [modalConfigSchema, toggleModalConfigSchema] = useState(false);
  const [modalDeleteSchema, toggleModalDeleteSchema] = useState(false);
  const { data: activeFleetSchema } = useGetAudioSchemaFleet(fleetId);
  const { mutate: deleteFleetSchema } = useDeleteAudioSchema(onDeleteSuccess, onDeleteFail);
  const { mutate: applyFleetSchema } = usePostApplyAudioSchemaToFleet(onApplySuccess, onApplyFail);

  const isDefaultSchema = item?.name === 'default';
  const isCurrentSchemaForFleet = item?._id === activeFleetSchema?.audioBundleId;
  const isCurrentSchemaActive = activeFleetSchema?.isActive;

  if (!item) {
    return (<>No schema</>);
  }

  const handleEditSchema = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    toggleModalEditSchema(true);
  };

  const handleConfigSchema = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    toggleModalConfigSchema(true);
  };
  const handleDeleteSchema = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    toggleModalDeleteSchema(true);
  };

  const applyToFleet = (): void => {
    applyFleetSchema({
      fleetId,
      audioBundleId: item._id,
    });
  };

  const deleteSchemaAction = (): void => {
    if (canEdit && item) {
      deleteFleetSchema({
        fleetId,
        audioSchemaId: item?._id,
      });
    }
    toggleModalDeleteSchema(false);
  };

  return (
    <>
      <ListItemWrapper noclick>
        <Tags.ItemOverview
          avatar={AudioThumbnail}
          avatarAlt="user"
          title={`${item.name}${isDefaultSchema ? ' (view only)' : ''}`}
          subtitle=""
          isRoundImage
        />
        {isCurrentSchemaForFleet && (
          <Tags.Status
            label={isCurrentSchemaActive ? 'Active schema' : 'Last active schema'}
            backgroundColor={isCurrentSchemaActive ? theme.colors.main_cta : theme.colors.inactive}
          />
        )}
        {/* <Tags.LabeledIcon
          label={`Created on ${getFormattedDate(item.createdAt)}`}
          icon={IconKeys.DATE}
          flexible
        />
        <Tags.LabeledIcon
          label={`Last update ${getFormattedDate(item.updatedAt)}`}
          icon={IconKeys.PULSE}
          flexible
        /> */}
        <div className="tags-container">
          <Button
            height="26px"
            width="120px"
            padding="8px 8px"
            background={theme.colors.outline}
            hoverBackground={theme.colors.inactive}
            disabled={!canEdit}
            onClick={applyToFleet}
          >
            Apply to fleet
          </Button>
          <Button
            height="26px"
            width="120px"
            padding="8px 8px"
            background={theme.colors.outline}
            hoverBackground={theme.colors.inactive}
            onClick={handleEditSchema}
          >
            {isDefaultSchema || !canEdit ? 'View' : 'Edit'}
          </Button>
          <Button
            height="26px"
            width="120px"
            padding="8px 8px"
            background={theme.colors.outline}
            hoverBackground={theme.colors.inactive}
            onClick={handleConfigSchema}
            disabled={item.name === 'default'}
          >
            Settings
          </Button>
          <Button
            height="26px"
            width="120px"
            padding="8px 8px"
            background={theme.colors.outline}
            hoverBackground={theme.colors.inactive}
            onClick={handleDeleteSchema}
            disabled={item.name === 'default'}
          >
            Remove
          </Button>
        </div>
      </ListItemWrapper>
      {modalEditSchema && (
        <Modal
          title={isDefaultSchema || !canEdit ? 'View schema' : 'Edit schema'}
          state={modalEditSchema}
          toggleState={toggleModalEditSchema}
        >
          <AlertSchema item={item} toggleModal={toggleModalEditSchema} readOnly={isDefaultSchema || !canEdit} />
        </Modal>
      )}
      {modalConfigSchema && (
        <Modal
          title="Schema settings"
          state={modalConfigSchema}
          toggleState={toggleModalConfigSchema}
        >
          <UpdateSchemaModal schema={item} onClose={() => toggleModalConfigSchema(false)} />
        </Modal>
      )}
      {modalDeleteSchema && (
        <Modal
          title="Delete schema"
          state={modalDeleteSchema}
          toggleState={toggleModalDeleteSchema}
          settings={{
            minWidth: '500px',
          }}
        >
          <DeleteModal
            title="Are you sure you want to delete this schema? All devices which are using it will be switched to default."
            onClose={() => toggleModalDeleteSchema(false)}
            onDelete={deleteSchemaAction}
          />
        </Modal>
      )}
    </>
  );
}

export default AlertAudioItem;
